// C.1 — фракционная механика Лив Лайфа «Передовая медицина».
//
// Правило (диздок v2.9):
//   Триггер на любое выставление СОЮЗНОГО юнита со стороны игрока за
//   Лив Лайф (любой unit_summoned / unit_played / unit_resurrected где
//   владелец — Герой livlife). Шаги:
//
//   1) Найти всех союзных юнитов на поле этого игрока (включая только что
//      выставленного).
//   2) Если есть раненые (current_hp < max_hp):
//        - Выбрать самого раненого по абсолютной разнице (max_hp - current_hp).
//          При равенстве — первый по позиции (1-я линия слева направо, потом
//          2-я слева направо).
//        - Полностью восстановить ему hp до max_hp.
//   3) Если все союзники в полном hp:
//        - Герой Лив Лайфа получает +2 hp (без overheal — cap по maxHp).
//   4) НЕ срабатывает на выставление Героя в начале матча (isInitialPlacement).
//
// Хук вызывается из event'а onUnitSummoned (общая шина в deus/hooks.js).
// Чтобы не вмешиваться в существующий код деуса/синтетикса, добавляем
// late-binding через registerUnitSummonedExtraHandler — Лив Лайф
// зарегистрирует свой хендлер при загрузке index.js.

import { healUnit } from '../abilities/livlife/heal.js';

export const LIV_FRONTLINE_SOURCE = { id: 'livlife_frontline', label: 'Передовая медицина' };

// Все союзные юниты на поле игрока (без Героя). Включает только что
// выставленного юнита (он уже в field[][]).
function getAlliedUnits(player) {
  const result = [];
  if (!player?.field) return result;
  for (let line = 0; line < 2; line++) {
    const row = player.field[line];
    if (!row) continue;
    for (let slot = 0; slot < row.length; slot++) {
      const u = row[slot];
      if (!u) continue;
      if (!u.isAlive || !u.isAlive()) continue;
      result.push({ unit: u, line, slot });
    }
  }
  return result;
}

// Выбор самого раненого. При равенстве — порядок позиции (line0 left→right,
// line1 left→right).
function pickMostWounded(units) {
  let bestEntry = null;
  let bestDiff = -1;
  for (const entry of units) {
    const u = entry.unit;
    const cur = u.current?.hp ?? 0;
    const max = u.base?.hp ?? cur;
    const diff = max - cur;
    if (diff <= 0) continue;
    if (diff > bestDiff) {
      bestDiff = diff;
      bestEntry = entry;
    }
  }
  return bestEntry; // { unit, line, slot } или null если все на full
}

// Поднять Герою hp на amount (cap по maxHp).
function bumpHeroHp(state, ownerIdx, amount) {
  const hero = state.players?.[ownerIdx]?.hero;
  if (!hero) return 0;
  const before = hero.hp ?? 0;
  const max = hero.maxHp ?? hero.base?.hp ?? before;
  const after = Math.min(max, before + amount);
  const gained = after - before;
  if (gained > 0) {
    hero.hp = after;
    if (state.log) {
      const name = state.players[ownerIdx].name || '?';
      state.log.add(`💊 Передовая медицина → Герой [${name}]: +${gained} hp (все союзники на full)`);
    }
  }
  return gained;
}

// Основной триггер. Вызывается извне (см. ниже регистрация).
//   summonedUnit — юнит, попавший на поле (или null если воскрешение/...).
//   опции:
//     options.isInitialPlacement — true для стартовой постановки Героя/юнитов
//       (в этой ситуации хук не срабатывает).
export function livLifeFrontlineMedicine(state, ownerIdx, summonedUnit, options = {}) {
  if (!state?.players) return;
  const player = state.players[ownerIdx];
  if (!player?.hero) return;
  if (player.hero.faction !== 'livlife') return;
  if (options?.isInitialPlacement) return;
  if (summonedUnit?.isInitialPlacement) return;

  const allies = getAlliedUnits(player);
  if (allies.length === 0) {
    // Только что выставленный юнит должен быть на поле; защитная ветка.
    bumpHeroHp(state, ownerIdx, 2);
    return;
  }

  const wounded = pickMostWounded(allies);
  if (wounded) {
    const u = wounded.unit;
    const cur = u.current?.hp ?? 0;
    const max = u.base?.hp ?? cur;
    const amount = Math.max(0, max - cur);
    if (amount > 0) {
      healUnit(state, u, amount, LIV_FRONTLINE_SOURCE);
    }
    return;
  }

  // Все на full → +2 hp Герою.
  bumpHeroHp(state, ownerIdx, 2);
}

// Регистрация в общем хуке onUnitSummoned. Делается из init-файла фракции.
// Чтобы не плодить cycle, импорт hooks делается отложенно.
export async function registerLivLifeFrontlineMedicine() {
  const hooks = await import('../abilities/deus/hooks.js');
  if (typeof hooks.registerUnitSummonedExtraHandler === 'function') {
    hooks.registerUnitSummonedExtraHandler((state, ownerIdx, summonedUnit) => {
      livLifeFrontlineMedicine(state, ownerIdx, summonedUnit);
    });
  }
}
